import { useMemo } from "react";
import {
  Button,
  Input,
  SelectSearch,
  Form,
  ModalFooter,
} from "@telluscompany/design-system";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalization } from "@i18n";
import { createStructureSchema, type StructureFormData } from "../../schemas";
import { useListComplexesQuery } from "../../../complexes/api/complexes/complexes.queries";
import type { ListComplexesResponse } from "../../../complexes/api/complexes/complexes.types";

interface CreateStructureFormProps {
  tenantId: string;
  onSubmit: (data: StructureFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const CreateStructureForm = ({
  tenantId,
  onSubmit,
  onCancel,
  isSubmitting,
}: CreateStructureFormProps) => {
  const { translate } = useLocalization();

  const { data: complexesData, isLoading: isLoadingComplexes } =
    useListComplexesQuery(tenantId, { limit: 100 });

  const complexOptions = useMemo(
    () =>
      complexesData?.data?.map((complex: ListComplexesResponse) => ({
        label: complex.name,
        value: complex.id,
      })) ?? [],
    [complexesData],
  );

  const form = useForm<StructureFormData>({
    resolver: zodResolver(createStructureSchema(translate)),
    defaultValues: {
      complexId: "",
      name: "",
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="px-[32px] py-[16px] max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <SelectSearch
                control={form.control}
                label={translate(
                  "management.structures.form.label.complex",
                )}
                name="complexId"
                placeholder={translate(
                  "management.structures.form.placeholder.complexSelect",
                )}
                options={complexOptions}
                disabled={isLoadingComplexes}
              />
            </div>
            <div>
              <Input
                control={form.control}
                label={translate("management.structures.form.label.name")}
                name="name"
                type="text"
                placeholder={translate(
                  "management.structures.form.placeholder.name",
                )}
              />
            </div>
          </div>
        </div>
        <ModalFooter
          footer={
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {translate("management.structures.button.save")}
            </Button>
          }
          closeModal={
            <Button type="button" variant="secondary" onClick={onCancel}>
              {translate("management.structures.button.cancel")}
            </Button>
          }
        />
      </form>
    </Form>
  );
};
